import { useState } from 'react';
import { Link } from 'react-router-dom';

const DISMISS_KEY = 'constare_dismissed_profile_banner_v1';

/**
 * Soft nudge shown on the dashboard for users who have not filled in their
 * first name yet. Dismissible per browser (best-effort, localStorage-based;
 * OK to lose because it just means the banner re-appears on next visit).
 *
 * Not blocking. Users can always ignore it.
 */
export default function CompleteProfileBanner({ firstName }: { firstName: string | null }) {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Only show if the user has no first name AND has not dismissed the banner.
  if (dismissed) return null;
  if (firstName && firstName.trim().length > 0) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // localStorage may be blocked (e.g. private mode). Silent fallback: the
      // banner will re-appear next session, which is acceptable.
    }
  };

  return (
    <div
      className="mb-6 flex items-center justify-between gap-4 rounded-lg border border-amber/40 bg-amber/5 px-4 py-3"
      data-testid="banner-complete-profile"
    >
      <div className="text-sm">
        <span className="font-medium text-text">Complete your profile.</span>{' '}
        <span className="text-muted">
          Add your first and last name so we can address you properly.
        </span>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <Link
          to="/profile"
          className="rounded-md border border-amber bg-amber/10 px-3 py-1.5 text-xs text-amber hover:bg-amber/20"
          data-testid="link-complete-profile"
        >
          Add name
        </Link>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="text-muted hover:text-text text-lg leading-none"
          data-testid="button-dismiss-profile-banner"
        >
          ×
        </button>
      </div>
    </div>
  );
}
